import { createSelector } from 'reselect'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import {
  isHotelOrderItem,
  isItemCancelled,
  isLPCHotelOrderItem,
  haveOrdersBeenFetched,
} from 'lib/order/orderUtils'
import {
  evaluateRecentSearchHotelCarousel,
  normalizeRecentSearchHotelCountryCode,
  getRecentSearchHotelCoordinates,
  type RecentSearchHotelPropertyCountry,
  type RecentSearchHotelPurchaseSummary,
  type RecentSearchHotelStay,
} from 'home/recentSearchHotelCarousel/recentSearchHotelCarouselModel'
import { getActiveLocale } from 'selectors/placeSelectors'
import { getPlaceCachePartition } from 'lib/places/placeCacheUtils'

const EXCLUDED_PURCHASE_STATUSES = new Set([
  'cancelled',
  'refunded',
  'abandoned',
  'payment_failed',
])

function formatStayDate(value: string | { format: (pattern: string) => string } | undefined): string | undefined {
  if (!value) return undefined
  if (typeof value === 'string') return value.slice(0, 10)
  return value.format(ISO_DATE_FORMAT)
}

function stayFromLeHotelItem(item: App.OrderItem): RecentSearchHotelStay | undefined {
  if (isItemCancelled(item) || item.status === 'failed') return undefined
  if (!item.reservation || item.reservation.state === 'cancelled') return undefined
  if (!item.reservation.startDate || !item.reservation.endDate) return undefined

  const countryCode = normalizeRecentSearchHotelCountryCode(item.offer.property?.geoData?.countryCode)
  const coordinates = getRecentSearchHotelCoordinates(item.offer.property?.latitude, item.offer.property?.longitude)
  const propertyId = (countryCode && coordinates) || !('propertyId' in item.package)
    ? undefined
    : item.package.propertyId

  return {
    checkIn: item.reservation.startDate,
    checkOut: item.reservation.endDate,
    ...(countryCode ? { countryCode } : {}),
    ...(coordinates ? { coordinates } : {}),
    ...(propertyId ? { propertyId } : {}),
  }
}

/**
 * Hotel stays that can hide a recent-search carousel.
 * Country searches match by country code; narrower searches match canonical places at the searched level.
 * Use structured country codes rather than location labels or legacy Google place ids.
 */
export function summariseOrderForRecentSearchHotel(order: App.Order): RecentSearchHotelPurchaseSummary {
  const stays: Array<RecentSearchHotelStay> = []

  for (const item of order.items) {
    if (!isHotelOrderItem(item) && !isLPCHotelOrderItem(item)) continue
    const stay = stayFromLeHotelItem(item)
    if (stay) stays.push(stay)
  }

  for (const item of order.bedbankItems) {
    if (item.status !== 'completed') continue
    const checkIn = formatStayDate(item.checkIn)
    const checkOut = formatStayDate(item.checkOut)
    if (!checkIn || !checkOut) continue
    const countryCode = normalizeRecentSearchHotelCountryCode(item.offer.property.address.countryCode)
    const coordinates = getRecentSearchHotelCoordinates(item.offer.property.latitude, item.offer.property.longitude)
    stays.push({
      checkIn,
      checkOut,
      ...(countryCode ? { countryCode } : {}),
      ...(coordinates ? { coordinates } : {}),
      ...((!countryCode || !coordinates) && item.propertyId ? { propertyId: item.propertyId } : {}),
    })
  }

  for (const item of order.accommodationV2Items) {
    if (item.isShadow || item.status === 'cancelled' || item.status === 'failed') continue
    if (!item.checkIn || !item.checkOut) continue
    stays.push({
      checkIn: item.checkIn,
      checkOut: item.checkOut,
      ...(item.propertyId ? { propertyId: item.propertyId } : {}),
    })
  }

  return {
    countsAsPurchase: order.orderSuccess && !EXCLUDED_PURCHASE_STATUSES.has(order.status),
    stays,
  }
}

function propertyCountryFromRecord(
  record: { fetching?: boolean, error?: unknown, property?: App.Property.Property } | undefined,
): RecentSearchHotelPropertyCountry {
  if (!record || record.fetching) return { status: 'loading' }
  if (record.error || !record.property) return { status: 'failed' }
  return {
    status: 'ready',
    countryCode: normalizeRecentSearchHotelCountryCode(record.property.location.countryCode),
    coordinates: getRecentSearchHotelCoordinates(record.property.location.latitude, record.property.location.longitude),
  }
}

const EMPTY_PURCHASES: Array<RecentSearchHotelPurchaseSummary> = []
const EMPTY_PROPERTY_COUNTRIES: Record<string, RecentSearchHotelPropertyCountry> = {}

export const getRecentSearchHotelPurchases = createSelector(
  (state: App.State) => state.orders.orders,
  (orders): Array<RecentSearchHotelPurchaseSummary> => {
    const values = Object.values(orders)
    if (values.length === 0) return EMPTY_PURCHASES
    return values.map(summariseOrderForRecentSearchHotel)
  },
)

export const getRecentSearchHotelPropertyCountries = createSelector(
  (state: App.State) => state.offer.properties,
  (properties): Record<string, RecentSearchHotelPropertyCountry> => {
    const entries = Object.entries(properties)
    if (entries.length === 0) return EMPTY_PROPERTY_COUNTRIES
    return Object.fromEntries(entries.map(([propertyId, record]) => [
      propertyId,
      propertyCountryFromRecord(record),
    ]))
  },
)

const getSearchPlaces = createSelector(
  (state: App.State) => state.destination.placeCacheByLocale,
  getActiveLocale,
  getPlaceCachePartition,
)

export const getRecentSearchHotelCarouselDecision = createSelector(
  (state: App.State) => state.auth.account.memberId,
  (state: App.State) => state.search.userRecentSearches,
  (state: App.State) => haveOrdersBeenFetched(state),
  (state: App.State) => getRecentSearchHotelPurchases(state),
  getSearchPlaces,
  getRecentSearchHotelPropertyCountries,
  (state: App.State) => state.destination.coordsToPlace,
  (_state: App.State, now: number) => now,
  (_state: App.State, _now: number, today: string) => today,
  (
    memberId,
    searches,
    ordersFetched,
    purchases,
    searchPlaces,
    propertyCountries,
    bookingPlacesByCoords,
    now,
    today,
  ) => evaluateRecentSearchHotelCarousel({
    memberId,
    searches,
    ordersFetched,
    purchases,
    searchPlaces,
    propertyCountries,
    bookingPlacesByCoords,
    now,
    today,
  }),
)
